/**
 * Permanent top status bar for the Snake (Tram) game.
 *
 * Mirrors `FroggerTopBar` visually — same chip pill design, same height —
 * but exposes five Snake-appropriate stats:
 *
 *   PONT  (score)   • KOCSI (cars)   • PÁLYA (board / screen)   •
 *   IDŐ   (time)    • ÉLET  (lives)
 *
 * The chip set is fixed but the third chip retargets per mode so the same
 * widget works for Classic, Adventure, Stations, and Free Roam runs.
 *
 * `update({ score, cars, board, time, lives })` is the only mutator — call
 * from `SnakeScene._updateHud()` whenever a stat changes. Strings are
 * passed verbatim so the caller can preformat e.g. `board` as `"1/4"` or
 * `time` as `"00:28"`.
 */

import { Container, Graphics, Text, TextStyle } from '../../../vendor/pixi.min.mjs';
import { t } from '../../config/i18n.js';
import { paintChip, darken } from '../../ui/shared.js';

const BAR_H = 110;
// Top bar background is fully transparent now — the snake.background
// theme image painted by `SnakeScene._createThemeBackground` shows
// straight through so the chips appear to float over the world.
const BAR_BG_COLOR = 0x000000;
const BAR_BG_ALPHA = 0.0;

const CHIP_W = 178;
const CHIP_H = 78;
const CHIP_GAP = 14;
// Sharp-cornered chips — per UX direction the in-game top header
// uses 0-radius corners across all games so the strip reads as a
// single uniform billboard. Mirrors `FroggerTopBar` and the back
// pill in `shared.js`.
const CHIP_RADIUS = 0;
const CHIP_LEFT_MARGIN = 20;
const CHIP_TOP_MARGIN = (BAR_H - CHIP_H) / 2;

const ICON_SIZE = 40;
const ICON_BG_RADIUS = 28;
const ICON_BG_X = CHIP_H / 2 - 2;

const TEXT_LEFT = CHIP_H + 4;

const LABEL_STYLE = new TextStyle({
    fontFamily: 'Poppins, Arial, sans-serif',
    fontSize: 13,
    fontWeight: '800',
    fill: '#ffffff',
    letterSpacing: 1.2,
    dropShadow: { color: '#000000', alpha: 0.3, distance: 1, blur: 1 },
});

const VALUE_STYLE = new TextStyle({
    fontFamily: 'Poppins, Arial, sans-serif',
    fontSize: 26,
    fontWeight: '900',
    fill: '#ffffff',
    dropShadow: { color: '#000000', alpha: 0.4, distance: 1, blur: 2 },
});

/** Static chip layout. `labelKey` may be overridden at update() time.
 *  Order matters — chips render left-to-right and the TIME chip
 *  hides itself in Classic / Adventure / Stations (only Free Roam
 *  uses the timer), so any chip placed AFTER 'time' would float
 *  off into the minimap's territory once 'time' goes invisible.
 *  LIVES is always visible so it sits directly after BOARD/SCREEN
 *  and TIME is intentionally last. */
const CHIP_DEFS = [
    { id: 'score', labelKey: 'common.stat_score', color: 0xfbbf24, iconSlot: 'snake.hud.score' },
    { id: 'cars',  labelKey: 'common.stat_cars',  color: 0xec4899, iconSlot: 'snake.hud.cars'  },
    { id: 'board', labelKey: 'common.stat_board', color: 0xa855f7, iconSlot: 'snake.hud.board' },
    // LIVES chip — added per client request to surface the per-stage
    // life pool. Has no shipped HUD asset yet, so when the theme
    // sprite slot is empty `_buildChip` draws a red Graphics heart on
    // the medallion as a fallback. Slot key is reserved so themers
    // can drop in a custom icon later without code changes. Sits
    // BEFORE the time chip so it always renders right next to
    // PÁLYA — the time slot is reclaimed by the minimap in
    // Classic / Adventure / Stations runs.
    { id: 'lives', labelKey: 'common.stat_lives', color: 0xef4444, iconSlot: 'snake.hud.lives', fallbackShape: 'heart' },
    { id: 'time',  labelKey: 'common.stat_time',  color: 0xfb923c, iconSlot: 'snake.hud.timer' },
];

// ── LIVES chip pulse-on-life-loss ──────────────────────────
// `pulseLives()` triggers a punch-scale + red flash overlay on the
// lives chip so the player gets unmissable visual feedback that
// they just lost a life. `tick()` advances the animation each
// frame from `SnakeScene`'s main update loop.
/** Total animation duration. Long enough to read, short enough not
 *  to overlap a second hit. */
const LIVES_PULSE_MS          = 600;
/** Peak scale at the middle of the pulse. The chip grows to this
 *  multiple of its resting size and bounces back. */
const LIVES_PULSE_PEAK_SCALE  = 1.35;
/** Peak alpha of the bright-red flash overlay painted on top of
 *  the chip during the pulse. */
const LIVES_PULSE_FLASH_ALPHA = 0.55;
/** Bright-red overlay colour — slightly oranger than the chip's
 *  own red so the flash reads as a fresh hit rather than a
 *  saturation bump of the chip body. */
const LIVES_PULSE_FLASH_COLOR = 0xff3030;

export class SnakeTopBar {
    /** @type {Container} */
    container = new Container();

    /** Static height — exported for the scene's layout math. */
    static HEIGHT = BAR_H;

    _game = null;
    _chips = {};

    /** Resting (x, y) of the LIVES chip — captured in `_build` so
     *  `tick()` can counter-translate during the pulse animation
     *  and the chip visually scales from its centre rather than its
     *  top-left corner. Other chips don't pulse so we don't need to
     *  remember their bases. */
    _livesBaseX = 0;
    _livesBaseY = 0;
    /** Timestamp the most recent `pulseLives()` call kicked off the
     *  scale-punch. `0` means no pulse is in flight. */
    _livesPulseStartedAt = 0;

    constructor(game) {
        this._game = game;
        this._build();
    }

    get height() { return BAR_H; }

    /**
     * Update visible chip values. Caller should pre-format `board` and `time`.
     * Pass `boardLabelKey` to retarget the third chip per mode, e.g.
     *   'common.stat_screen' (Adventure)  →  PÁLYA / SCREEN
     *   'common.stat_target' (Free Roam)  →  CÉL / TARGET
     *
     * @param {{
     *   score: number|string,
     *   cars:  number|string,
     *   board: number|string,
     *   time:  number|string,
     *   lives?: number|string,
     *   boardLabelKey?: string
     * }} stats
     */
    update(stats) {
        if (this._chips.score) this._chips.score._valueText.text = String(stats.score ?? 0);
        if (this._chips.cars)  this._chips.cars._valueText.text  = String(stats.cars  ?? 0);
        if (this._chips.board) {
            this._chips.board._valueText.text = String(stats.board ?? 0);
            if (stats.boardLabelKey && this._chips.board._labelText) {
                this._chips.board._labelText.text = t(stats.boardLabelKey);
            }
        }
        if (this._chips.time) {
            this._chips.time._valueText.text = String(stats.time ?? '0:00');
            this._chips.time.visible = stats.time != null && stats.time !== '';
        }
        if (this._chips.lives) {
            // Render `null` / `undefined` as 0 so the chip never
            // shows blank — easier to read than an empty number.
            this._chips.lives._valueText.text = String(stats.lives ?? 0);
        }
    }

    /**
     * Trigger a one-shot scale-punch + red flash on the LIVES chip
     * so the player gets unmissable visual feedback that they just
     * lost a life. Re-entrant: calling again mid-pulse simply
     * restarts the animation from t=0, which is the right behaviour
     * for back-to-back hits during invincibility lapses.
     */
    pulseLives() {
        if (!this._chips.lives) return;
        this._livesPulseStartedAt = Date.now();
    }

    /**
     * Per-frame animation tick. Currently only advances the
     * lives-chip pulse — pure no-op when nothing is animating, so
     * cheap to call unconditionally from `SnakeScene.update`.
     *
     * The pulse profile is a single sin-arch (0 → 1 → 0 over
     * `LIVES_PULSE_MS`) driving both the scale-punch and the flash
     * overlay alpha, so the brightness peaks at the same moment the
     * chip is biggest.
     */
    tick() {
        const lives = this._chips.lives;
        if (!lives) return;

        const startedAt = this._livesPulseStartedAt;
        if (startedAt <= 0) return;

        const elapsed = Date.now() - startedAt;
        if (elapsed >= LIVES_PULSE_MS) {
            // Animation finished — snap the chip back to its resting
            // state and clear the pulse marker so subsequent frames
            // hit the cheap early-return above.
            lives.scale.set(1);
            lives.x = this._livesBaseX;
            lives.y = this._livesBaseY;
            if (lives._flashOverlay) lives._flashOverlay.alpha = 0;
            this._livesPulseStartedAt = 0;
            return;
        }

        // 0..1 across the pulse duration → sin-arch goes 0 → 1 → 0
        // with the peak at exactly the midpoint, giving a clean
        // "punch out, snap back" beat.
        const t01 = elapsed / LIVES_PULSE_MS;
        const punch = Math.sin(t01 * Math.PI);

        const scale = 1 + (LIVES_PULSE_PEAK_SCALE - 1) * punch;
        // Counter-translate so the visual centre stays put while
        // the chip scales — without this the chip would visibly
        // drift down-and-right because Pixi scales from (0,0).
        const dx = (1 - scale) * CHIP_W / 2;
        const dy = (1 - scale) * CHIP_H / 2;
        lives.scale.set(scale);
        lives.x = this._livesBaseX + dx;
        lives.y = this._livesBaseY + dy;

        if (lives._flashOverlay) {
            lives._flashOverlay.alpha = LIVES_PULSE_FLASH_ALPHA * punch;
        }
    }

    setVisible(v) {
        this.container.visible = !!v;
    }

    destroy() {
        this.container.removeChildren();
        if (this.container.parent) this.container.parent.removeChild(this.container);
        this._game = null;
        this._chips = {};
    }

    _build() {
        const w = this._game.width;

        if (BAR_BG_ALPHA > 0) {
            const bg = new Graphics();
            bg.rect(0, 0, w, BAR_H);
            bg.fill({ color: BAR_BG_COLOR, alpha: BAR_BG_ALPHA });
            this.container.addChild(bg);
        }

        let x = CHIP_LEFT_MARGIN;
        for (const def of CHIP_DEFS) {
            const chip = this._buildChip(def);
            chip.x = x;
            chip.y = CHIP_TOP_MARGIN;
            this.container.addChild(chip);
            this._chips[def.id] = chip;
            if (def.id === 'lives') {
                // Stash the resting position so `tick()` can
                // counter-translate during the scale-punch — keeps
                // the chip pulsing from its centre instead of
                // ballooning outward from the top-left corner.
                this._livesBaseX = chip.x;
                this._livesBaseY = chip.y;
            }
            x += CHIP_W + CHIP_GAP;
        }
    }

    _buildChip(def) {
        const chip = new Container();

        // v2 chip bevel: dark rim → main fill → faux gradient highlight
        // on the top half → faded white inner stroke. The chip graphics
        // group lives at (0,0); text + icon are positioned relative to
        // that. `shadowAlpha: 0` drops the drop-shadow per UX direction
        // so the squared-off top header reads as a flat ribbon
        // (mirrors `FroggerTopBar`).
        const face = paintChip({
            w: CHIP_W,
            h: CHIP_H,
            r: CHIP_RADIUS,
            color: def.color,
            shadowAlpha: 0,
        });
        chip.addChild(face);

        // Icon medallion — solid white-ish circle with a darker stroke so
        // the pictogram pops off the chip body. The darker stroke is a
        // tone of the chip colour, keeping the medallion visually tied
        // to its host chip (per v2 mockup).
        const rim = darken(def.color, 0.55);
        const iconBg = new Graphics();
        iconBg.circle(ICON_BG_X, CHIP_H / 2, ICON_BG_RADIUS);
        iconBg.fill({ color: 0xffffff, alpha: 0.92 });
        iconBg.stroke({ color: rim, width: 3 });
        chip.addChild(iconBg);

        const icon = this._game.theme?.createSprite(def.iconSlot, ICON_SIZE, ICON_SIZE, {
            anchorX: 0.5, anchorY: 0.5, fitMode: 'contain',
        });
        if (icon) {
            icon.x = ICON_BG_X;
            icon.y = CHIP_H / 2;
            chip.addChild(icon);
        } else if (def.fallbackShape === 'heart') {
            // Drawn fallback used when no themed icon ships for the
            // chip — currently only the LIVES chip relies on it.
            // Two overlapping circles + a downward triangle, rendered
            // with a bold dark-red stroke so the heart reads clearly
            // on the white medallion (the previous flat-red fill
            // blended into the red chip body once anti-aliasing
            // softened the medallion edge — the stroke now does the
            // heavy lifting for definition). Sized larger than the
            // other chip icons (0.5 vs 0.42 of ICON_SIZE) so it
            // matches the visual weight of the trophy / tram / board
            // sprites that fill their medallions edge-to-edge.
            const cx = ICON_BG_X;
            const cy = CHIP_H / 2;
            const s = ICON_SIZE * 0.50;          // half-width of one lobe pair
            const lobeY = cy - s * 0.18;         // lobe centres sit slightly above mid
            const lobeR = s * 0.62;
            const lobeOff = s * 0.55;
            const FILL_COLOR   = 0xef4444;       // brand red — same as chip
            const STROKE_COLOR = 0x7f1d1d;       // dark red — pops on white
            const STROKE_W     = 3;

            // Lobes — two overlapping filled circles. Stroked
            // separately from the triangle so the inner overlap
            // edges between the two lobes don't get re-stroked
            // (which would look like a crease).
            const lobes = new Graphics();
            lobes.circle(cx - lobeOff, lobeY, lobeR);
            lobes.circle(cx + lobeOff, lobeY, lobeR);
            lobes.fill({ color: FILL_COLOR, alpha: 1 });
            lobes.stroke({ color: STROKE_COLOR, width: STROKE_W });
            chip.addChild(lobes);

            // Triangle bottom — apex at cy + s*0.95 so the heart's
            // pointy tip sits well below the medallion midpoint.
            // The top edge runs through the lobe centres so it
            // visually fuses with the lobes into a single heart.
            const tri = new Graphics();
            tri.moveTo(cx - s, lobeY);
            tri.lineTo(cx + s, lobeY);
            tri.lineTo(cx,     cy + s * 0.95);
            tri.closePath();
            tri.fill({ color: FILL_COLOR, alpha: 1 });
            tri.stroke({ color: STROKE_COLOR, width: STROKE_W });
            chip.addChild(tri);
        }

        const label = new Text({ text: t(def.labelKey), style: LABEL_STYLE });
        label.x = TEXT_LEFT;
        label.y = 14;
        chip.addChild(label);

        const value = new Text({ text: '0', style: VALUE_STYLE });
        value.x = TEXT_LEFT;
        value.y = 34;
        chip.addChild(value);

        chip._labelText = label;
        chip._valueText = value;

        // Pulse-on-life-loss overlay — only attached to the LIVES
        // chip (or any future chip flagged with `fallbackShape: 'heart'`).
        // Lives ABOVE the label/value so the flash visually wraps
        // both the heart and the lives count.
        if (def.fallbackShape === 'heart') {
            const flash = new Graphics();
            flash.rect(0, 0, CHIP_W, CHIP_H);
            flash.fill({ color: LIVES_PULSE_FLASH_COLOR, alpha: 1 });
            flash.alpha = 0;            // hidden until pulseLives() fires
            chip.addChild(flash);
            chip._flashOverlay = flash;
        }

        return chip;
    }
}
